import { isWrapSourceEligibleShape } from '@/lib/object-effects/text-wrap'
import { boxVerticesForShapeGeometry, localVecToCanvas } from '@/lib/transforms/geometry'
import type { WrapRect } from './types/wrap-rect'
import type { WrapShapeCandidate } from './types/wrap-shape-candidate'
import type { WrapTextBoxCandidate } from './types/wrap-text-box-candidate'
import type { SideOffsets } from './types/side-offsets'
import type { WrapTextBoxSourceCandidate } from './types/wrap-text-box-source-candidate'

/** Axis-aligned rectangle intersection (no rotation handling — callers gate on rotation being ~0
 *  before relying on this for wrap purposes; see `shapeAffectsTextBox`). */
export function axisAlignedRectsOverlap(a: WrapRect, b: WrapRect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
}

const ROTATION_EPSILON = 0.01

export function isEffectivelyUnrotated(rotation: number | undefined): boolean {
  if (rotation == null) return true

  const normalized = ((rotation % 360) + 360) % 360

  return normalized < ROTATION_EPSILON || 360 - normalized < ROTATION_EPSILON
}

/**
 * Axis-aligned bounding box (canvas space) enclosing `rect` after rotating it about its own
 * center by `rect.rotation` degrees — the ordinary "bounding box" of a tilted rectangle, always
 * larger than (or equal to, if unrotated) the shape's own tilted footprint. Used both for the
 * coarse overlap gate below and for the actual Bounding Box exclusion band built in
 * `compute-wrap-obstacles.ts` — Wrap Shape doesn't need this since `shape-contour-wrap.ts`
 * rotates the real contour polygon directly, no AABB approximation required.
 */
export function rotatedAabb(rect: WrapRect & { rotation?: number }): WrapRect {
  if (isEffectivelyUnrotated(rect.rotation)) {
    return { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
  }

  const corners = boxVerticesForShapeGeometry({
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    rotation: rect.rotation ?? 0,
  })
  const xs = corners.map((c) => c.x)
  const ys = corners.map((c) => c.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)

  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY }
}

/**
 * The four corners of the Bounding Box wrap obstacle for a (possibly rotated) rect: `rect`
 * expanded on each of its own LOCAL sides (left/top/right/bottom) by `offsets`, THEN rotated about
 * the ORIGINAL (unpadded) rect's own center — the same pivot `boxVerticesForShapeGeometry` rotates
 * the plain shape about. Deliberately returns the rotated QUADRILATERAL itself, not an axis-aligned
 * box enclosing it: collapsing to an AABB (as an earlier version of this function did, and as
 * `rotatedAabb` above still does for the coarse overlap gate) throws away the rotation exactly
 * where it matters — the caller must scanline-intersect text rows against these four actual
 * corners/edges (see `computeRotatedBoundingBoxBands` in `compute-wrap-obstacles.ts`), not against
 * a bigger enclosing rectangle that either over-blocks the rect's own now-empty AABB corners (at
 * 45°, the AABB is `side * sqrt(2)` wide, well past the rect's real diagonal edges) or mis-swaps
 * which canvas edge an offset lands on (e.g. at 45° with a top-only offset, padding a canvas-axis
 * edge pushes the exclusion zone straight up instead of out along the rect's own, now-diagonal, top
 * edge — "top offset" stops tracking the rect's actual top side the moment it isn't axis-aligned).
 *
 * Expanding pre-rotation and rotating about the unpadded center is exactly equivalent to "push each
 * side of the already-rotated rectangle outward along its own normal, keeping the same rotation":
 * rotation is a rigid transform, so it correctly carries an asymmetric offset's recentering shift
 * (e.g. top-only padding shifts the rect's center along its own local up axis) around with the
 * rotation instead of applying that shift in fixed canvas directions. At rotation 0 the returned
 * quadrilateral IS the plain padded axis-aligned rectangle (in corner-list form), so a caller that
 * special-cases the unrotated fast path still gets byte-identical results to the old single-band
 * math.
 */
export function rotatedRectOffsetCorners(
  rect: WrapRect & { rotation?: number },
  offsets: SideOffsets
): Array<{ x: number; y: number }> {
  const cx = rect.x + rect.width / 2
  const cy = rect.y + rect.height / 2
  const hw = rect.width / 2
  const hh = rect.height / 2

  const localCorners = [
    { x: -hw - offsets.left, y: -hh - offsets.top },
    { x: hw + offsets.right, y: -hh - offsets.top },
    { x: hw + offsets.right, y: hh + offsets.bottom },
    { x: -hw - offsets.left, y: hh + offsets.bottom },
  ]

  const rotation = rect.rotation ?? 0

  return localCorners.map((corner) => {
    const rotated = localVecToCanvas(corner, rotation)

    return { x: cx + rotated.x, y: cy + rotated.y }
  })
}

/**
 * True when `shape` should cause `textBox` to reflow around it: shape is a wrap-source-eligible
 * shape (a plain wrap-eligible kind, OR a group carrying its own `wrapStyle` — see
 * `isWrapSourceEligibleShape`) with `wrapStyle: 'boundingBox'`, `'wrap'`, or `'jump'`, the text box
 * itself is axis-aligned (a rotated receiving text box is out of scope for v1 — its own content
 * would need to render at an angle too), and their bounding boxes actually overlap. A shape's own
 * wrapStyle applies regardless of grouping — being inside a group (at any nesting depth) does not
 * disqualify it as a source; a group's own wrapStyle and a member's own wrapStyle are independent
 * settings that can both be active at once (see the group leaf-decomposition branch in
 * `rawBandsForTextBox`, `compute-wrap-obstacles.ts`, for the group-level case). Deliberately
 * independent of z-order/layering — wrap is a property of the shape's own setting, not of which
 * element happens to be drawn on top, so bring-to-front/send-to-back never toggles wrap on or off.
 * `zIndexOf` is accepted for call-site compatibility with other per-element z-index lookups but is
 * not used here. A rotated shape is handled via its axis-aligned envelope (`rotatedAabb`) for all
 * three styles for the coarse overlap gate here — the actual band shape (computed by
 * `rawBandsForTextBox` in `compute-wrap-obstacles.ts`, not this function) still follows the real
 * rotated contour for a plain shape's Wrap Shape, and for a group's Wrap Shape decomposes into each
 * of its own leaf members' contour/rect independently rather than using the group's single outer
 * bounding box (that stays exclusive to Bounding Box/Jump). Self-excluded by id, same as
 * `textBoxAffectsTextBox` — text-unification made a text frame just a `ShapeData` carrying
 * `paragraphs`, so without this a frame with its own `wrapStyle` set (a legitimate, independent
 * setting meant to wrap *other* text around it) would find itself in `shapes` and wrap around its
 * own 100%-overlapping rect, blanking every row of its own text.
 */
export function shapeAffectsTextBox(
  shape: WrapShapeCandidate,
  textBox: WrapTextBoxCandidate,
  _zIndexOf: (id: string) => number | undefined
): boolean {
  if (shape.id === textBox.id) return false
  if (shape.hidden || textBox.hidden) return false
  if (!isWrapSourceEligibleShape(shape)) return false
  if (shape.wrapStyle !== 'boundingBox' && shape.wrapStyle !== 'wrap' && shape.wrapStyle !== 'jump')
    return false
  if (!isEffectivelyUnrotated(textBox.rotation)) return false

  if (!isEffectivelyUnrotated(shape.rotation)) {
    return axisAlignedRectsOverlap(rotatedAabb(shape), textBox)
  }

  return axisAlignedRectsOverlap(shape, textBox)
}

/**
 * True when text box `source` should cause `target` to reflow around it — same rules as
 * `shapeAffectsTextBox` (wrap style, overlapping) minus the shape kind check (every text box is
 * trivially wrap-eligible as a source), plus self-exclusion: a text box never wraps around itself.
 * A source's own wrapStyle applies regardless of grouping, same as `shapeAffectsTextBox`.
 * Deliberately independent of z-order/layering, same reasoning as `shapeAffectsTextBox`; `zIndexOf`
 * is accepted for call-site compatibility but not used here. `target` must still be axis-aligned (a
 * rotated receiving text box is out of scope for v1); a rotated `source` is handled via its
 * axis-aligned envelope (`rotatedAabb`), same as a rotated shape source. `'wrap'` is accepted
 * alongside `'boundingBox'` — a text box is always a plain rectangle, so a contour-based wrap is
 * geometrically identical to its bounding box (see `bandFromRect` in `compute-wrap-obstacles.ts`,
 * which builds the same band either way); a text box shouldn't silently no-op just because the
 * panel happens to be set to "Wrap Shape". `'jump'` is accepted the same way — a text box source
 * set to Jump excludes the same full-width band a jump-styled shape would (see `jumpBandFromRect`
 * in `compute-wrap-obstacles.ts`).
 */
export function textBoxAffectsTextBox(
  source: WrapTextBoxSourceCandidate,
  target: WrapTextBoxCandidate,
  _zIndexOf: (id: string) => number | undefined
): boolean {
  if (source.id === target.id) return false
  if (source.hidden || target.hidden) return false
  if (
    source.wrapStyle !== 'boundingBox' &&
    source.wrapStyle !== 'wrap' &&
    source.wrapStyle !== 'jump'
  )
    return false
  if (!isEffectivelyUnrotated(target.rotation)) return false

  if (!isEffectivelyUnrotated(source.rotation)) {
    return axisAlignedRectsOverlap(rotatedAabb(source), target)
  }

  return axisAlignedRectsOverlap(source, target)
}
